// ════════════════════════════════════════════════════════════════════
// TERROR — H6 · REPLAYS
// ────────────────────────────────────────────────────────────────────
// "Record the match action-log to Firebase, replay through the
//  Shatterfield renderer at 1x/2x."  — the roadmap
//
// ── THE FINDING THAT MADE THIS SMALL ─────────────────────────────────
// Terror has no action log, and it does not need one. Every board
// mutation in the entire game already funnels through exactly three
// functions in gameManager — updateMatch, updatePlayer, endMatch — and
// every one of them is a Firebase `update()` payload. That payload IS
// the action log. It is already a diff, it is already ordered, and it
// is already the complete truth about the board, because the renderer
// has never read anything else.
//
// So a replay is not a parallel recording of "what happened." It is the
// write-ahead log of the match, and playing it back is replaying the
// writes. Nothing can drift, because there is no second description of
// the game to drift from.
//
// ── THE SECOND FINDING: THE RENDERER NEEDS NO REWRITE ────────────────
// State reaches the Shatterfield through ONE call:
//
//     listenToMatch(code, (data) => setMatch(data))
//
// A replay is therefore a function with that exact signature which
// happens to read from an array instead of a socket. `makeReplayFeed()`
// below returns one. Driving the real renderer off a replay is a single
// conditional at the listener, not a second renderer — and the frames
// arrive as whole match objects, exactly as Firebase delivers them.
//
// ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────
// No compression, no delta-of-delta, no binary packing. A duel is a few
// hundred small updates. Measure before optimising something that is
// already under a quarter of a megabyte — and the SIZE GUARD below
// means a pathological match refuses to save rather than quietly
// writing four megabytes to somebody's profile.
//
// ── STORAGE ──────────────────────────────────────────────────────────
//   games/terror/replays/{uid}/{replayId}/
//     meta   : { code, at, mode, oppName, won, turns, frames, bytes, v }
//     seed   : the full match object at the first frame
//     frames : [ { t, p?, u } ]   t = ms since seed, p = path prefix,
//                                  u = the raw update payload
//
// Under the player's own uid, not under matches/, because a match node
// is deleted when the room closes and a replay has to outlive the room.
// ⚠ IT LIVES IN THE DRAWER, AND IT DID NOT USED TO. This shipped on a
// new TOP-LEVEL node and a wrong belief that IE_FIREBASE_LAW §1's
// new-field exemption covered it. §1 is about new fields under
// profiles/{uid} — which genuinely have no $extra guard — and says
// nothing about top-level nodes, which §2 forbids outright and which
// rules.json denies with a `"$other": { ".read": false, ".write": false }`
// catch-all. Every read and write here would have failed
// permission_denied on the live database, silently, because this layer
// is fire-and-forget by design. Moved under games/terror, where the
// $gameId wildcard already grants authed read+write — zero rules deploy.

import { ref, get, set, remove, query, orderByKey } from "firebase/database";
import { db, GAME_BASE } from "./firebase";

export const REPLAY_SCHEMA = 2;

// ── DIALS ─────────────────────────────────────────────────────────────
// Tune with a controller in hand, not in theory.
export const MAX_FRAMES = 4000;        // a runaway guard, not a rule
export const MAX_BYTES = 900 * 1024;   // refuse rather than write a monster
export const KEEP_REPLAYS = 20;        // per player; oldest pruned on save
export const SPEEDS = [1, 2];          // caP'n asked for 1x/2x. Exactly two.

// ── THE PATH WALK ─────────────────────────────────────────────────────
// Firebase's update() takes keys that may themselves be paths
// ("players/0/anima"), merges shallowly at the LAST segment, and
// replaces whatever was there. Replaying a frame has to do the same
// thing or the reconstruction is a different game.
//
// This is the one piece of real logic in the file and it is the one
// piece that can be silently wrong, which is why replay.selftest.mjs
// exists and why it tests THIS.
const setDeep = (root, segments, value) => {
  let node = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const k = segments[i];
    // Objects are cloned on the way down so a replayed frame never
    // mutates the previous frame — a consumer holding frame 12 while
    // frame 13 renders must not watch frame 12 change under it.
    node[k] = (node[k] && typeof node[k] === "object") ? (Array.isArray(node[k]) ? [...node[k]] : { ...node[k] }) : {};
    node = node[k];
  }
  node[segments[segments.length - 1]] = value;
};

// ── THE KEY ESCAPE (2026-09-08) ───────────────────────────────────────
// ⚠ EVERY REPLAY IN THE GAME FAILED TO SAVE, AND SAID SO IN A WARNING
// NOBODY WAS READING. Terror writes Firebase MULTI-PATH updates all over
// the place — `{ "solrendReady/0": true }`, `{ "field/allOrNothingActive":
// false }`, `{ "players/1/khao": 3 }`. Firebase accepts those as a WRITE:
// the slash is a path separator. It refuses them as DATA: a stored key
// may not contain . # $ / [ ] at all. The recorder stored the update
// object verbatim, so the very first frame of every duel — the ready
// handshake, which fires before anyone has played a card — poisoned the
// whole record and commitReplay came back:
//
//   [replay] save failed (non-fatal): ... contains an invalid key
//   (solrendReady/0) ... Keys must be non-empty strings and can't
//   contain ".", "#", "$", "/", "[", or "]"
//
// "Non-fatal" is doing a lot of work in that sentence: it is fatal to
// the feature. Found by playing one duel to the end and reading the
// console, which is the only way it could have been found — no test
// asserts on what Firebase will accept.
//
// The fix is a reversible escape, JSON-Pointer style, applied on the way
// in and undone on the way out. `~` is legal in a Firebase key, and
// escaping `~` FIRST is what makes the round trip exact for an id that
// already contains one (a sealed Necropolis id looks like `champ_a~0~1`).
const KEY_ESC = [["~", "~t"], ["/", "~s"], [".", "~d"], ["#", "~h"], ["$", "~m"], ["[", "~l"], ["]", "~r"]];
export const encodeFrameKey = (k) =>
  KEY_ESC.reduce((acc, [raw, esc]) => acc.split(raw).join(esc), String(k));
export const decodeFrameKey = (k) =>
  [...KEY_ESC].reverse().reduce((acc, [raw, esc]) => acc.split(esc).join(raw), String(k));

export function applyFrame(state, frame) {
  const next = { ...(state || {}) };
  const prefix = frame.p ? String(frame.p).split("/").filter(Boolean) : [];
  for (const [key, value] of Object.entries(frame.u || {})) {
    // Decode BEFORE splitting: the slash a multi-path update meant as a
    // separator is restored here and then split on, exactly as it was
    // before the escape existed. A key with no escape decodes to itself,
    // so replays recorded either way replay identically.
    const path = decodeFrameKey(key).split("/").filter(Boolean);
    setDeep(next, [...prefix, ...path], value);
  }
  return next;
}

// Reconstruct the board as of frame index `i` (inclusive). Pure.
export function stateAt(replay, i) {
  let s = replay?.seed ? JSON.parse(JSON.stringify(replay.seed)) : {};
  const frames = replay?.frames || [];
  for (let k = 0; k <= i && k < frames.length; k++) s = applyFrame(s, frames[k]);
  return s;
}

// ════════════════════════════════════════════════════════════════════
// THE RECORDER
// ────────────────────────────────────────────────────────────────────
// A module singleton, because there is exactly one live duel per client
// and gameManager's three write functions have no other way to reach a
// recorder without every call site growing a parameter.
//
// It is OFF by default and turns itself off on commit. A recorder that
// keeps running after a duel is a memory leak wearing a feature's coat.
// ════════════════════════════════════════════════════════════════════

let R = null;   // { code, t0, seed, frames, over, mode }

// ── THE PLAYBACK LOCK ─────────────────────────────────────────────────
// One lever that makes the whole renderer read-only.
//
// During playback the Shatterfield is a live React tree with live
// buttons. Auditing 700KB of component for "is this interactive" is the
// expensive version of this problem and it would be wrong the first
// time somebody adds a button. The cheap version is exact: every board
// mutation in Terror goes through updateMatch / updatePlayer / endMatch
// (that is the same finding this whole file is built on), so locking
// those three makes a tap a no-op no matter what the player presses.
// Local optimistic state is corrected by the very next frame.
//
// SAFETY: startRecording() clears the lock unconditionally. A lock left
// on by a crashed theatre would silently break the next real duel —
// that is a bug that must be impossible, not a bug that must be
// remembered.
let LOCKED = false;
export const isPlaybackLocked = () => LOCKED;
export const setPlaybackLock = (on) => { LOCKED = !!on; };

export const isRecording = () => !!R;

export function startRecording(code, { mode = "pvp" } = {}) {
  LOCKED = false;   // a live duel is never locked; see THE PLAYBACK LOCK
  R = { code, t0: Date.now(), seed: null, frames: [], over: false, mode };
}

export function stopRecording() { R = null; }

// The seed is taken from the FIRST full match object the renderer ever
// sees, not from createMatch — because a joiner never calls createMatch,
// and a reconnect starts mid-duel. Whatever the renderer started from is
// by definition what the replay must start from.
export function seedRecording(match) {
  if (!R || R.seed || !match) return;
  R.seed = JSON.parse(JSON.stringify(match));
  R.t0 = Date.now();
}

// Called by gameManager on every write. `path` is the prefix the update
// applies at (null for the match root).
export function recordUpdate(code, path, updates) {
  if (!R || R.code !== code || R.over || !updates) return;
  if (!R.seed) return;                       // nothing to diff against yet
  if (R.frames.length >= MAX_FRAMES) {
    if (R.frames.length === MAX_FRAMES) {
      console.warn(`[replay] frame cap ${MAX_FRAMES} hit — this duel will save truncated.`);
      R.frames.push({ t: Date.now() - R.t0, u: { __truncated: true } });
    }
    return;
  }
  // A SHALLOW COPY, not the caller's object. Every caller passes a
  // literal today, but a frame that holds a live reference is a frame
  // that can change after it was recorded — and the resulting replay
  // would be subtly, unreproducibly wrong, which is the worst kind.
  // One object spread per board write is not a cost worth thinking
  // about. Found in the critique pass.
  // ⚠ AND THE KEYS ARE ESCAPED, not just copied — see THE KEY ESCAPE.
  // A shallow spread preserved the multi-path keys verbatim and Firebase
  // rejected the whole document at save time.
  const u = {};
  for (const [k, v] of Object.entries(updates)) u[encodeFrameKey(k)] = v;
  const f = { t: Date.now() - R.t0, u };
  if (path) f.p = path;
  R.frames.push(f);
}

// ── THE SAVE ──────────────────────────────────────────────────────────
// Called once, at match end. Returns { ok, id } or { ok:false, reason }.
// Never throws into the end-of-match path: a replay that fails to save
// is a replay you do not have. A crash here would be a duel you cannot
// leave.
export async function commitReplay(uid, { won = null, oppName = "", turns = 0 } = {}) {
  if (!R) return { ok: false, reason: "not-recording" };
  const rec = R;
  R = null;                                  // stop first, always
  if (!uid) return { ok: false, reason: "no-uid" };
  if (!rec.seed || !rec.frames.length) return { ok: false, reason: "empty" };

  const payload = {
    meta: {
      v: REPLAY_SCHEMA,
      code: rec.code || "",
      at: rec.t0,
      mode: rec.mode,
      oppName: oppName || "",
      won: won === null ? null : !!won,
      turns: turns || 0,
      frames: rec.frames.length,
      ms: (rec.frames[rec.frames.length - 1]?.t) || 0,
    },
    seed: rec.seed,
    frames: rec.frames,
  };

  // SIZE GUARD — measured, not guessed, and it refuses rather than
  // truncating: half a duel presented as a whole one is worse than no
  // duel at all.
  let bytes = 0;
  try { bytes = JSON.stringify(payload).length; } catch (_) { return { ok: false, reason: "unserializable" }; }
  payload.meta.bytes = bytes;
  if (bytes > MAX_BYTES) {
    console.warn(`[replay] ${(bytes / 1024) | 0}KB exceeds the ${(MAX_BYTES / 1024) | 0}KB budget — not saved.`);
    return { ok: false, reason: "too-large", bytes };
  }

  // The id sorts chronologically as a string, so orderByKey IS
  // newest-last and the prune below needs no sort of its own.
  const id = `${rec.t0}_${(rec.code || "solo").toLowerCase()}`;
  try {
    await set(ref(db, `${GAME_BASE}/replays/${uid}/${id}`), payload);
    await pruneReplays(uid);
    return { ok: true, id, bytes };
  } catch (e) {
    console.warn("[replay] save failed (non-fatal):", e?.message);
    return { ok: false, reason: "write-failed" };
  }
}

// Keep the newest KEEP_REPLAYS. Deletion is loud in the console and
// silent to the player — a shelf that quietly grows forever is how a
// profile ends up eight megabytes deep.
export async function pruneReplays(uid) {
  if (!uid) return;
  try {
    const snap = await get(query(ref(db, `${GAME_BASE}/replays/${uid}`), orderByKey()));
    const val = snap.val() || {};
    const ids = Object.keys(val).sort();
    const excess = ids.length - KEEP_REPLAYS;
    if (excess <= 0) return;
    for (const id of ids.slice(0, excess)) {
      await remove(ref(db, `${GAME_BASE}/replays/${uid}/${id}`));
    }
    console.log(`[replay] pruned ${excess} old replay(s).`);
  } catch (e) {
    console.warn("[replay] prune failed (non-fatal):", e?.message);
  }
}

// ── THE SHELF ─────────────────────────────────────────────────────────
// Index only — meta, never frames. A shelf that downloads twenty full
// duels to draw twenty rows is a shelf nobody opens twice.
export async function listReplays(uid) {
  if (!uid) return [];
  try {
    const snap = await get(ref(db, `${GAME_BASE}/replays/${uid}`));
    const val = snap.val() || {};
    return Object.entries(val)
      .map(([id, r]) => ({ id, ...(r.meta || {}) }))
      .sort((a, b) => (b.at || 0) - (a.at || 0));
  } catch (e) {
    console.warn("[replay] list failed:", e?.message);
    return [];
  }
}

export async function loadReplay(uid, id) {
  if (!uid || !id) return null;
  try {
    const snap = await get(ref(db, `${GAME_BASE}/replays/${uid}/${id}`));
    return snap.val() || null;
  } catch (e) {
    console.warn("[replay] load failed:", e?.message);
    return null;
  }
}

export async function deleteReplay(uid, id) {
  if (!uid || !id) return false;
  try { await remove(ref(db, `${GAME_BASE}/replays/${uid}/${id}`)); return true; }
  catch (e) { console.warn("[replay] delete failed:", e?.message); return false; }
}

// ════════════════════════════════════════════════════════════════════
// THE FEED — a listenToMatch with a tape in it
// ────────────────────────────────────────────────────────────────────
//   const feed = makeReplayFeed(replay, { speed: 2 });
//   const unsub = feed.subscribe((match) => setMatch(match));
//   feed.play(); feed.pause(); feed.seek(120); feed.setSpeed(1);
//
// `subscribe` has listenToMatch's exact contract: it fires immediately
// with the current state and returns its own unsubscribe. That is the
// whole reason the Shatterfield needs one conditional and not a second
// renderer.
//
// TIMING: frames carry their real elapsed offset, so a replay breathes
// at the pace the duel was actually played — including the eleven
// seconds somebody spent staring at a Nihil. Gaps longer than
// MAX_GAP_MS are compressed, because nobody wants to re-live a
// bathroom break at 1x.
// ════════════════════════════════════════════════════════════════════

export const MAX_GAP_MS = 2500;   // a dial

export function makeReplayFeed(replay, { speed = 1 } = {}) {
  const frames = replay?.frames || [];
  const subs = new Set();

  // Precompute every state once. A duel is a few hundred frames of
  // small objects; recomputing from the seed on every scrub is the kind
  // of quadratic nobody notices until the scrubber stutters.
  const states = [];
  {
    let s = replay?.seed ? JSON.parse(JSON.stringify(replay.seed)) : {};
    states.push(s);
    for (const f of frames) { s = applyFrame(s, f); states.push(s); }
  }

  // Compressed schedule: index i plays at schedule[i] ms of replay time.
  const schedule = [0];
  for (let i = 0; i < frames.length; i++) {
    const gap = Math.min(MAX_GAP_MS, Math.max(0, (frames[i].t || 0) - (i ? frames[i - 1].t || 0 : 0)));
    schedule.push(schedule[i] + gap);
  }

  let idx = 0;                  // states index: 0 = seed, n = after frame n-1
  let playing = false;
  let rate = SPEEDS.includes(speed) ? speed : 1;
  let timer = null;

  const emit = () => { for (const cb of subs) { try { cb(states[idx]); } catch (_) { /* one bad consumer never stops the tape */ } } };

  const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };

  const step = () => {
    clear();
    if (!playing || idx >= states.length - 1) { playing = false; emitMeta(); return; }
    const wait = Math.max(16, (schedule[idx + 1] - schedule[idx]) / rate);
    timer = setTimeout(() => { idx++; emit(); emitMeta(); step(); }, wait);
  };

  // A second, lighter channel for the scrubber — position changes far
  // more often than anything wants a whole match object re-rendered.
  const metaSubs = new Set();
  const emitMeta = () => {
    const m = { idx, total: states.length - 1, playing, speed: rate,
                ms: schedule[idx], totalMs: schedule[schedule.length - 1] };
    for (const cb of metaSubs) { try { cb(m); } catch (_) {} }
  };

  return {
    // listenToMatch's contract, exactly.
    subscribe(cb) {
      subs.add(cb);
      try { cb(states[idx]); } catch (_) {}
      return () => subs.delete(cb);
    },
    onProgress(cb) {
      metaSubs.add(cb);
      emitMeta();
      return () => metaSubs.delete(cb);
    },
    play() { if (idx >= states.length - 1) idx = 0; playing = true; emit(); emitMeta(); step(); },
    pause() { playing = false; clear(); emitMeta(); },
    toggle() { playing ? this.pause() : this.play(); },
    seek(i) {
      idx = Math.max(0, Math.min(states.length - 1, i | 0));
      emit(); emitMeta();
      if (playing) step();
    },
    setSpeed(s) { rate = SPEEDS.includes(s) ? s : 1; emitMeta(); if (playing) step(); },
    stateAtIndex: (i) => states[Math.max(0, Math.min(states.length - 1, i | 0))],
    length: states.length - 1,
    destroy() { playing = false; clear(); subs.clear(); metaSubs.clear(); },
  };
}

const replayApi = {
  REPLAY_SCHEMA, MAX_FRAMES, MAX_BYTES, KEEP_REPLAYS, SPEEDS, MAX_GAP_MS,
  applyFrame, stateAt, isRecording, isPlaybackLocked, setPlaybackLock,
  startRecording, stopRecording,
  seedRecording, recordUpdate, commitReplay, pruneReplays,
  listReplays, loadReplay, deleteReplay, makeReplayFeed,
};
export default replayApi;
